import {
  OnboardingGuide,
  type OnboardingGuideProps
} from "@prismicio/editor-fields"
import { ActionListItem } from "@prismicio/editor-ui"
import type { ReactNode } from "react"

import { getRepositoryName } from "../../app/routing"
import { useEnvironments } from "../../app/services/environments"
import { useRepository } from "../../app/services/repository"
import {
  useIsSharedOnboardingEnabled,
  useSharedOnboarding
} from "../../app/services/sharedOnboarding"
import { trackSharedOnboardingGuide } from "../../app/services/tracking"
import type { OnboardingStep as PageBuilderOnboardingStep } from "../../onboarding/OnboardingSteps"
import { useShouldDisplayOnboarding as useShouldPBDisplayOnboarding } from "../../onboarding/useShouldDisplayOnboarding"
import { useSharedOnboardingParams } from "./useSharedOnboardingParams"

interface DocumentListSharedOnboardingProps {
  fallback?: ReactNode
}

export function DocumentListSharedOnboarding(
  props: DocumentListSharedOnboardingProps
) {
  const isSharedOnboardingEnabled = useIsSharedOnboardingEnabled()
  const { currentEnvironment: env } = useEnvironments()

  const isEnvProd = env ? env.kind === "prod" : true
  if (!isSharedOnboardingEnabled || !isEnvProd) return null
  return <DocumentListSharedOnboardingContent {...props} />
}

export function DocumentListSharedOnboardingContent(
  props: DocumentListSharedOnboardingProps
) {
  const { fallback } = props
  const { onboarding, toggleStep, toggleGuide } = useSharedOnboarding()
  const stepOverrides = useStepOverrides()

  if (!onboarding) return null

  if (onboarding.isDismissed) {
    return (
      <ActionListItem
        textVariant="normal"
        backgroundColor="transparent"
        startIcon="notStarted"
        onClick={toggleGuide}
      >
        Get started
      </ActionListItem>
    )
  }

  const repositoryName = getRepositoryName()

  return (
    <OnboardingGuide
      tracking={{ track: trackSharedOnboardingGuide, source: "PageBuilder" }}
      onboardingState={onboarding}
      onToggleGuide={toggleGuide}
      onToggleStep={toggleStep}
      fallback={fallback}
      stepOverrides={stepOverrides}
      projectName={repositoryName}
      repositoryName={repositoryName}
    />
  )
}

function useStepOverrides(): OnboardingGuideProps["stepOverrides"] | undefined {
  const { starterId } = useRepository()
  const isPBOnboardingEnabled = useShouldPBDisplayOnboarding()
  const { navigateWithStepParam } = useSharedOnboardingParams()

  if (!isPBOnboardingEnabled) return undefined

  function goToPageBuilderStep(step: PageBuilderOnboardingStep) {
    navigateWithStepParam("/onboarding", step)
  }

  if (starterId) {
    return {
      // TODO: Refactor to render a link instead of a using a click action (DT-2432)
      // https://github.com/prismicio/editor/pull/1369#discussion_r1824728583
      chooseLocale: () => goToPageBuilderStep("chooseMainLanguage"),
      createProject: () => goToPageBuilderStep("launchStarter"),
      setupSliceMachine: () => goToPageBuilderStep("launchStarter")
    }
  }

  return {
    // TODO: Refactor to render a link instead of a using a click action (DT-2432)
    // https://github.com/prismicio/editor/pull/1369#discussion_r1824728583
    chooseLocale: () => goToPageBuilderStep("chooseMainLanguage"),
    createProject: () => goToPageBuilderStep("createProject"),
    setupSliceMachine: () => goToPageBuilderStep("setupSliceMachine")
  }
}
